import { apiFetch, apiFetchEnvelope, type ListMeta } from "@core/http";
import type { components } from "@core/types/openapi";
import { listEntries } from "../catalogs/api";

export type PointMovement = components["schemas"]["PointMovement"];
export type PointRule = components["schemas"]["PointRule"];
export type CatalogEntry = components["schemas"]["CatalogEntry"];

// G-001/RF-MIE-12: el filtro por origen conserva únicamente acción propia
// del miembro y acción de un referido — RF-MIE-12 (DEF-star-circle.md:347) no
// respalda canje ni ajuste como valores filtrables. `PointMovement.origin`
// sigue siendo el enum completo de cuatro valores del contrato (G-002): acá
// se acota el filtro, no el dato.
export type OriginFilter = "all" | "own" | "referral";

export interface MovementsPage {
  items: PointMovement[];
  balance: number;
  // EXTRA-A: el contrato declara `historicalAccumulated` como requerido en
  // la respuesta, pero la resolución humana del 2026-09-06 retira su
  // presentación de la cabecera del portal — ese requisito es RF-BEN-14, de
  // la superficie app, no RF-MIE-11 del portal. Se tipa para no perder el
  // dato del contrato; deliberadamente no se renderiza en ningún lugar de
  // PointsPanel.
  historicalAccumulated: number;
  meta: ListMeta;
}

const movementsBase = (memberId: string) =>
  `/v1/portal/members/${encodeURIComponent(memberId)}/movements`;
const movementBase = (id: string) =>
  `/v1/portal/movements/${encodeURIComponent(id)}`;

export async function listMovements(
  memberId: string,
  query: { origin: OriginFilter; cursor?: string },
): Promise<MovementsPage> {
  const params = new URLSearchParams();
  if (query.origin !== "all") params.set("origin", query.origin);
  if (query.cursor) params.set("cursor", query.cursor);
  const suffix = params.size ? `?${params}` : "";
  const envelope = await apiFetchEnvelope<{
    items: PointMovement[];
    balance: number;
    historicalAccumulated: number;
  }>(`${movementsBase(memberId)}${suffix}`);
  return {
    items: envelope.data.items,
    balance: envelope.data.balance,
    historicalAccumulated: envelope.data.historicalAccumulated,
    meta: envelope.meta ?? { next: null, total: envelope.data.items.length },
  };
}

export interface RegisterPointsInput {
  actionId: string;
  date: string;
  reference?: string;
  evidence?: string;
  confirmed?: boolean;
}

export const registerPointsAction = (
  memberId: string,
  input: RegisterPointsInput,
  idempotencyKey: string,
) =>
  apiFetch<PointMovement>(`${movementsBase(memberId)}`, {
    method: "POST",
    body: input,
    idempotencyKey,
  });

export interface VoidMovementInput {
  adjustmentReasonId: string;
  detail: string;
}
export interface VoidMovementResult {
  voided?: PointMovement;
  offsettingEntry?: PointMovement;
}

export const voidMovement = (
  movementId: string,
  input: VoidMovementInput,
  idempotencyKey: string,
) =>
  apiFetch<VoidMovementResult>(`${movementBase(movementId)}/void`, {
    method: "POST",
    body: input,
    idempotencyKey,
  });

// G-005/SPEC-007 §2: el registro manual solo ofrece acciones cuya regla es
// de asignación manual — ofrecer una automática permitiría otorgar a mano
// los mismos puntos que el motor ya otorgó por evento (doble acreditación).
export async function listManualMemberRules(): Promise<PointRule[]> {
  const envelope = await apiFetchEnvelope<{
    rules: PointRule[];
  }>("/v1/portal/points-engine/rules?scope=member&active=true");
  return envelope.data.rules.filter((rule) => rule.assignment === "manual");
}

// El catálogo de motivos de ajuste ya existe (`catalogs/api.ts`): reusarlo
// evita una cuarta copia de la URL de catálogos (ya hay tres: catalogs/,
// members/, users/).
export const listAdjustmentReasons = () =>
  listEntries("adjustment-reasons", { includeInactive: false, search: "" });

// Mismo patrón que vehicles/api.ts: los catálogos de uso interno del portal
// llegan como string plano; los que ve el miembro llegan como { es, en }
// (ADR-007).
export const catalogLabel = (entry: CatalogEntry) =>
  typeof entry.name === "string" ? entry.name : entry.name.es;

// SPEC-007 §2 / RN-05: la regla aplicable se evalúa contra la fecha
// indicada, no la de registro. Entre las reglas manuales de la acción, se
// prefiere la que cubre esa fecha (`validFrom`/`validUntil`, comparación
// lexicográfica válida sobre fechas ISO). `listManualMemberRules` solo trae
// reglas activas *hoy*: si ninguna de ellas cubre la fecha indicada (p. ej.
// una fecha pasada cuya regla histórica ya no está activa), el cliente no
// tiene con qué afirmar cuántos puntos se otorgarán — la regla realmente
// aplicable en esa fecha puede estar inactiva y ni siquiera haber llegado al
// cliente. Por eso este caso se etiqueta `approximate` en vez de devolver un
// número silencioso: quien llama decide cómo comunicar la incertidumbre, sin
// afirmar una cifra que el servidor podría no sostener. La verificación
// autoritativa la hace siempre el servidor (`NO_ACTIVE_RULE` si no hay
// ninguna regla vigente para esa fecha).
export type ManualRuleResolution =
  | { kind: "exact"; rule: PointRule }
  | { kind: "approximate"; rule: PointRule }
  | { kind: "none" };

export const resolveManualRule = (
  rules: PointRule[],
  actionId: string,
  date: string,
): ManualRuleResolution => {
  const candidates = rules.filter((rule) => rule.action.id === actionId);
  const withinRange = candidates.find(
    (rule) =>
      rule.validFrom <= date && (!rule.validUntil || date <= rule.validUntil),
  );
  if (withinRange) return { kind: "exact", rule: withinRange };
  if (candidates[0]) return { kind: "approximate", rule: candidates[0] };
  return { kind: "none" };
};
